import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { TwoNService } from './two-n.service';
import { Router } from '@angular/router';

export interface IncomingCallNotification {
  id: string;
  message: string;
  timestamp: number;
  callerInfo: Record<string, any>;
  hasVideo: boolean;
  isVisible: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class IncomingCallNotificationService {
  private currentNotificationSubject = new BehaviorSubject<IncomingCallNotification | null>(null);
  public currentNotification$ = this.currentNotificationSubject.asObservable();

  private incomingCallListener: any;
  private callEventListener: any;
  private inCall = false;
  private autoDismissTimeout: any = null;
  private activeCallId: string | null = null; // Track active call to prevent duplicate notifications

  constructor(
    private twoNService: TwoNService,
    private router: Router,
    private ngZone: NgZone
  ) {
    this.initializeIncomingCallListener();
  }

  private async initializeIncomingCallListener() {
    try {
      // Import the TwoNPlugin dynamically
      const { TwoNPlugin } = await import('@Vessel-Tech/2n-intercom');
      
      // Listen for incoming call events
      this.incomingCallListener = await (TwoNPlugin as any).addListener('twoNIncomingCall', (event: any) => {
        console.log('[IncomingCallNotification] Incoming call event received:', event);

        // IMPORTANT: Run inside Angular zone to trigger change detection
        // Native plugin callbacks run outside Angular's zone
        this.ngZone.run(() => {
          this.showIncomingCallNotification(event);
        });
      });
      
      // Also listen for call state changes to suppress notifications for active calls
      this.callEventListener = await (TwoNPlugin as any).addListener('twoNCallEvent', (event: any) => {
        console.log('[IncomingCallNotification] Call event received:', event);

        // Run inside Angular zone to ensure UI updates
        this.ngZone.run(() => {
          switch (event.type) {
            case 'connected':
              // A call is now active – prevent further notifications and hide any current one.
              this.inCall = true;
              this.activeCallId = `call-${event.timestamp || Date.now()}`;
              console.log('[IncomingCallNotification] Call connected – setting inCall flag and hiding notification');

              // Just hide the notification UI — do NOT decline the call
              if (this.currentNotificationSubject.value?.isVisible) {
                this.hideNotification();
              }

              // Ensure we're on the intercom page (fallback if answerCall navigation didn't complete)
              if (!this.router.url.includes('/intercom')) {
                console.log('[IncomingCallNotification] Not on intercom page, navigating...');
                this.router.navigate(['/intercom']);
              }
              break;

            case 'disconnected':
            case 'ended':
            case 'declined':
              // Call has finished – allow future notifications again.
              console.log('[IncomingCallNotification] Call finished – clearing inCall flag');
              this.inCall = false;
              this.activeCallId = null;
              break;

            default:
              // Other events are ignored for notification purposes.
              break;
          }
        });
      });
      
      console.log('[IncomingCallNotification] Call listeners initialized');
    } catch (error) {
      console.error('[IncomingCallNotification] Error initializing call listeners:', error);
    }
  }

  private showIncomingCallNotification(event: any) {
    // Check if we already have an active call - don't show notification for already connected calls
    if (this.inCall) {
      console.log('[IncomingCallNotification] inCall flag is true – skipping incoming call notification');
      return;
    }

    // Additional safeguard using legacy activeCallId check (kept for backwards compatibility)
    if (this.activeCallId) {
      console.log('[IncomingCallNotification] Ignoring incoming call notification - call already active');
      return;
    }
    
    // Check if we already have a notification visible - don't duplicate
    const current = this.currentNotificationSubject.value;
    if (current && current.isVisible) {
      console.log('[IncomingCallNotification] Notification already visible, not showing another one');
      return;
    }
    
    const notificationId = `call-${Date.now()}`;
    const notification: IncomingCallNotification = {
      id: notificationId,
      message: event.message || 'Incoming call from the Entry Portal',
      timestamp: event.timestamp || Date.now(),
      callerInfo: event.callerInfo || {},
      hasVideo: event.hasVideo || false,
      isVisible: true
    };

    console.log('[IncomingCallNotification] Showing notification immediately:', notification);
    
    // Update UI immediately
    this.currentNotificationSubject.next(notification);

    // Clear any existing timeout
    if (this.autoDismissTimeout) {
      clearTimeout(this.autoDismissTimeout);
    }

    // Auto-dismiss after 30 seconds if not answered
    this.autoDismissTimeout = setTimeout(() => {
      const current = this.currentNotificationSubject.value;
      if (current && current.id === notification.id && current.isVisible) {
        console.log('[IncomingCallNotification] Auto-dismissing notification after timeout');
        this.dismissNotification();
      }
    }, 30000);
  }

  async answerCall() {
    try {
      console.log('[IncomingCallNotification] User chose to answer call');

      const currentNotification = this.currentNotificationSubject.value;
      if (!currentNotification) {
        console.error('[IncomingCallNotification] No current notification to answer');
        return;
      }

      // Clear the auto-dismiss timeout
      if (this.autoDismissTimeout) {
        clearTimeout(this.autoDismissTimeout);
        this.autoDismissTimeout = null;
      }

      // Hide the notification immediately
      this.hideNotification();

      // Queue the native answer BEFORE navigation so the intercom page's
      // setupVideoView call can consume it immediately instead of falling back later.
      console.log('[IncomingCallNotification] Requesting native answer...');
      const answerPromise = this.twoNService.answerIncomingCall();

      console.log('[IncomingCallNotification] Navigating to /intercom...');
      await this.router.navigate(['/intercom']);
      console.log('[IncomingCallNotification] Navigation complete, awaiting native answer request...');

      const result = await answerPromise;
      console.log('[IncomingCallNotification] answerIncomingCall resolved:', result);
    } catch (error) {
      console.error('[IncomingCallNotification] Error in answerCall:', error);
    }
  }

  async declineCall() {
    try {
      console.log('[IncomingCallNotification] User chose to decline call');
      
      // Decline the call through the 2N service
      await this.twoNService.declineIncomingCall();
      
      // Dismiss the notification
      this.dismissNotification();
      
      console.log('[IncomingCallNotification] Call declined');
    } catch (error) {
      console.error('[IncomingCallNotification] Error declining call:', error);
      // Dismiss the notification even if there was an error declining
      this.dismissNotification();
    }
  }

  /**
   * Hide the notification UI without affecting call state.
   * Used when the call connects (already answered) or when navigating away.
   */
  hideNotification() {
    const current = this.currentNotificationSubject.value;
    if (current) {
      console.log('[IncomingCallNotification] Hiding notification (no call action)');

      if (this.autoDismissTimeout) {
        clearTimeout(this.autoDismissTimeout);
        this.autoDismissTimeout = null;
      }

      this.currentNotificationSubject.next({ ...current, isVisible: false });
      setTimeout(() => {
        this.currentNotificationSubject.next(null);
      }, 300);
    }
  }

  /**
   * Dismiss the notification AND decline the call.
   * Only used when the user hasn't answered yet (e.g., auto-dismiss timeout, swipe away).
   */
  async dismissNotification() {
    const current = this.currentNotificationSubject.value;
    if (current) {
      // Only decline if the call is not already connected
      if (!this.inCall) {
        console.log('[IncomingCallNotification] Dismissing notification - declining unanswered call');
        try {
          await this.twoNService.declineIncomingCall();
          console.log('[IncomingCallNotification] Call declined when dismissing notification');
        } catch (error) {
          console.error('[IncomingCallNotification] Error declining call when dismissing:', error);
        }
      } else {
        console.log('[IncomingCallNotification] Dismissing notification - call already connected, not declining');
      }

      this.hideNotification();
    }
  }

  async cleanup() {
    if (this.incomingCallListener) {
      await this.incomingCallListener.remove();
    }
    
    if (this.callEventListener) {
      await this.callEventListener.remove();
    }
    
    // Clear any pending timeouts
    if (this.autoDismissTimeout) {
      clearTimeout(this.autoDismissTimeout);
      this.autoDismissTimeout = null;
    }
  }
} 
